/**
 * Shared API types for the revamp (WP-P1). Backend contracts per
 * docs/revamp/BLUEPRINT.md §4, §5, §9 and WP-A. Endpoints marked "(new)" may
 * not exist yet — callers must degrade gracefully on 404.
 */

export type UserRole = 'DRIVER' | 'DISPATCHER' | 'MANAGER' | 'ADMIN' | 'SUPERADMIN'

/* ── Features (new) ─────────────────────────────────────────────────────── */

/** GET /features → { [key]: boolean } */
export type FeatureFlags = Record<string, boolean>

export const LEGACY_FEATURE_KEYS = [
  'legacy_dashboard',
  'legacy_assignments_page',
  'legacy_cascades_page',
  'legacy_duty_schedule',
] as const
export type LegacyFeatureKey = (typeof LEGACY_FEATURE_KEYS)[number]

/* ── Settings registry (new) ────────────────────────────────────────────── */

export type SettingType = 'boolean' | 'int' | 'float' | 'string' | 'enum' | 'time' | 'json'

export interface SettingRegistryEntry {
  key: string
  type: SettingType
  default: unknown
  description: string
  ui_group: string
  group_overridable: boolean
  deprecated?: boolean
  /** enum options, when type === 'enum' */
  options?: string[]
  min?: number
  max?: number
  step?: number
  label?: string
  unit?: string
}

export type SettingSource = 'default' | 'account' | 'group'

export interface SettingValue {
  key: string
  value: unknown
  source: SettingSource
}

/** GET /settings?group_id= → { [ui_group]: SettingValue[] } */
export type SettingsByGroup = Record<string, SettingValue[]>

/** PUT /settings body */
export interface SettingsUpdate {
  group_id?: string
  values: Record<string, unknown>
}

/* ── Onboarding (new) ───────────────────────────────────────────────────── */

export type OnboardingStepStatus = 'done' | 'todo' | 'skipped'

export interface OnboardingStep {
  key: string
  title: string
  status: OnboardingStepStatus
  href: string
  count?: number
  description?: string
}

export interface OnboardingStatus {
  steps: OnboardingStep[]
  progress_pct: number
}

/* ── Dispatch (new) ─────────────────────────────────────────────────────── */

export type CoverageState =
  | 'UNCOVERED' | 'PROPOSED' | 'ASSIGNED' | 'ACCEPTED' | 'IN_PROGRESS' | 'DONE'

export type TimingTier = 'CRITICAL' | 'LATE' | 'AT_RISK' | 'ON_TIME' | 'AHEAD' | 'KNOCK_ON'

export interface DispatchTodayRow {
  load_id: string
  load_number: string
  customer_name?: string | null
  first_delivery?: string | null
  planned_start?: string | null
  first_appointment?: string | null
  stops: number
  region?: string | null
  depot?: string | null
  equipment_needed?: string | null
  coverage: CoverageState
  driver_id?: string | null
  driver_name?: string | null
  proposal_confidence?: number | null
  timing?: TimingTier | null
  vehicle?: string | null
  trailer?: string | null
  risk_flags?: string[]
  status?: string
}

export interface DispatchTodaySummary {
  due: number
  uncovered: number
  proposals: number
  exceptions: number
}

/** GET /dispatch/today?date= */
export interface DispatchTodayResponse {
  date: string
  summary: DispatchTodaySummary
  loads: DispatchTodayRow[]
}

export type InboxItemType =
  | 'LATE_RISK' | 'KNOCK_ON' | 'DRIVER_DECLINED' | 'DRIVER_NO_SHOW' | 'DVIR_DEFECT'
  | 'HOS_BREACH_RISK' | 'UNCOVERED_LOAD' | 'TMS_QUARANTINE' | 'CUSTOMER_REFUSAL'
  | 'GEOFENCE_MISSING' | 'RED_DEBRIEF' | 'APPROVAL_PROPOSED_PLAN'

export type InboxAction =
  | 'APPROVE' | 'REASSIGN' | 'NOTIFY_CUSTOMER' | 'MESSAGE_DRIVER' | 'RESCHEDULE' | 'SNOOZE' | 'RESOLVE'

export interface InboxItem {
  id: string
  type: InboxItemType
  title: string
  what_happened: string
  impact?: string | null
  recommended_actions: InboxAction[]
  load_id?: string | null
  load_number?: string | null
  driver_id?: string | null
  driver_name?: string | null
  owner_id?: string | null
  owner_name?: string | null
  due_by?: string | null
  sla: 'ok' | 'warning' | 'breached'
  status: 'OPEN' | 'SNOOZED' | 'RESOLVED'
  created_at: string
}

/** GET /dispatch/inbox */
export interface InboxResponse {
  items: InboxItem[]
  total: number
}

/** POST /dispatch/inbox/{id}/action */
export interface InboxActionRequest {
  action: InboxAction
  driver_id?: string
  reason?: string
  snooze_until?: string
  reschedule_to?: string
}

/* ── Vehicles (new) ─────────────────────────────────────────────────────── */

export type VehicleType = 'TRUCK' | 'VAN' | 'TRACTOR' | 'TRAILER' | 'OTHER'

export interface Vehicle {
  id: string
  registration: string
  name?: string | null
  type: VehicleType
  equipment_type_id?: string | null
  equipment_type_name?: string | null
  logistics_group_id?: string | null
  capacity_kg?: number | null
  is_active: boolean
  defect_open?: boolean
  last_inspection_at?: string | null
  odometer_km?: number | null
}

export interface VehicleInput {
  registration: string
  name?: string | null
  type: VehicleType
  equipment_type_id?: string | null
  logistics_group_id?: string | null
  capacity_kg?: number | null
  is_active?: boolean
}

/* ── Auth / signup (new) ────────────────────────────────────────────────── */

export interface SignupRequest {
  company_name: string
  /** WP-C `POST /auth/signup` calls this field `name`. */
  name: string
  email: string
  password: string
  fleet_mode: 'PRIVATE_DISTRIBUTION' | 'FOR_HIRE_TRUCKLOAD'
  timezone: string
  /** ISO-3166 alpha-2; sets the default HOS rule pack. */
  country?: string
}

/** `POST /auth/signup` — account + ADMIN user + defaults + a live session. */
export interface SignupResponse {
  account_id?: string
  account_name?: string
  account_slug?: string
  slug?: string
  user_id?: string
  employee_id?: string
  login_hint?: string
  fleet_mode?: string
  subscription_tier?: string
  email_verification_required?: boolean
  email_sent?: boolean
  auto_verified?: boolean
  next?: string
  /** non-production only */
  verification_token?: string
  access_token?: string
  refresh_token?: string
  role?: string
  driver_name?: string
  /** kept for the WP-P1 scaffold's success copy */
  verification_required?: boolean
  message?: string
}

/* ── Existing search (used by the command palette) ──────────────────────── */

export interface LoadSearchHit {
  id: string
  load_number: string
  status?: string
  driver_name?: string | null
  driver_id?: string | null
  origin?: string | null
  destination?: string | null
  planned_start?: string | null
}

export interface Paged<T> {
  items: T[]
  total: number
  page?: number
  page_size?: number
}

/* ══ WP-P4 / WP-P6 additions ═══════════════════════════════════════════════
 * Shapes from docs/revamp/changelog_WP-C.md (shifts, inspections, imports,
 * invites, onboarding) and changelog_WP-B.md (driver acceptance / tasks).
 * Appended — nothing above this line is modified.
 */

/* ── Drivers: invites & funnel (WP-C §6) ───────────────────────────────── */

export type InviteStatus = 'NOT_INVITED' | 'INVITED' | 'ACCEPTED'

export interface DriverInviteRow {
  id: string
  employee_id: string
  full_name: string
  email?: string | null
  phone_number?: string | null
  invite_status?: InviteStatus | null
  invited_at?: string | null
  first_login_at?: string | null
}

/** GET /admin/driver-invites */
export interface DriverInvitesResponse {
  counts: { total: number; not_invited: number; invited: number; accepted: number; logged_in: number }
  drivers: DriverInviteRow[]
}

/** POST /admin/drivers/{id}/invite */
export interface DriverInviteResult {
  driver_id: string
  employee_id: string
  email?: string | null
  invite_status: InviteStatus
  invited_at?: string | null
  email_sent: boolean
  sms_sent: boolean
  sms_provider?: string | null
  /** non-production only */
  set_password_link?: string | null
  expires_at?: string | null
}

/** POST /admin/drivers/invite-all-pending */
export interface InviteAllPendingResult {
  invited: number
  emails_sent: number
  sms_sent: number
  skipped: { driver_id: string; employee_id: string; reason: string }[]
  results: DriverInviteResult[]
}

/* ── CSV import (WP-C §6, same envelope for drivers/facilities/vehicles) ── */

export interface ImportRowError {
  row: number
  sheet?: string | null
  field?: string | null
  message: string
}

export interface ImportResult {
  success: boolean
  entity: string
  created: number
  updated: number
  skipped: number
  errors: ImportRowError[]
  warnings?: (string | ImportRowError)[]
  /** file header → target field */
  mapping?: Record<string, string>
  rows_total?: number
  drivers_created?: number
  drivers_updated?: number
  invites_sent?: number
}

/** Target fields accepted by POST /admin/drivers/import. */
export const DRIVER_IMPORT_FIELDS = [
  'employee_id', 'full_name', 'email', 'phone_number', 'license_number', 'license_class',
  'license_expiry', 'base_location', 'logistics_group', 'driver_type', 'hos_country_code',
  'shift_start_time', 'shift_end_time', 'hire_date', 'invite',
] as const
export type DriverImportField = (typeof DRIVER_IMPORT_FIELDS)[number]
export const DRIVER_IMPORT_REQUIRED: DriverImportField[] = ['employee_id', 'full_name']

/* ── Shifts (WP-C §3) ──────────────────────────────────────────────────── */

export interface VehicleSummary {
  id: string
  registration: string
  vehicle_type?: string | null
  status?: string | null
  make?: string | null
  model?: string | null
  is_trailer?: boolean
}

export interface DriverShift {
  id: string
  driver_id: string
  driver_name?: string | null
  status: 'ON_DUTY' | 'ENDED'
  vehicle?: VehicleSummary | null
  trailer?: VehicleSummary | null
  started_at: string
  ended_at?: string | null
  start_odometer_km?: number | null
  end_odometer_km?: number | null
  start_inspection_id?: string | null
  end_inspection_id?: string | null
  notes?: string | null
  duration_minutes?: number | null
}

/** GET /shifts?driver_id=&date= */
export interface ShiftsResponse { shifts: DriverShift[]; count: number }

/* ── Inspections / DVIR (WP-C §4) ──────────────────────────────────────── */

export type InspectionOutcome = 'PASS' | 'DEFECT' | 'OUT_OF_SERVICE'

export interface InspectionItem {
  code: string
  label?: string | null
  result: 'PASS' | 'DEFECT' | 'NA'
  note?: string | null
  photo_ids?: string[]
}

export interface Inspection {
  id: string
  driver_id: string
  driver_name?: string | null
  vehicle_id?: string | null
  vehicle?: VehicleSummary | null
  shift_id?: string | null
  inspection_type: 'PRE_TRIP' | 'POST_TRIP'
  outcome: InspectionOutcome
  items: InspectionItem[]
  defect_count: number
  odometer_km?: number | null
  notes?: string | null
  signed_at?: string | null
  dispatch_task_id?: string | null
  created_at: string
}

/** GET /inspections?driver_id= */
export interface InspectionsResponse { inspections: Inspection[]; count: number }

/* ── Driver board (assembled client-side from 4 endpoints) ─────────────── */

export type HosLight = 'GREEN' | 'AMBER' | 'RED'

export interface BoardSegment {
  kind: 'DUTY' | 'DRIVE' | 'SERVICE' | 'BREAK'
  start: string
  end: string
  label: string
  breakKind?: string
}

export interface BoardLoadBar {
  id: string
  load_number: string
  status: string
  start: string
  end: string
  stop_count: number
}

export interface BoardRow {
  driverId: string
  driverName: string
  employeeId?: string | null
  baseLocation?: string | null
  trafficLight: HosLight | null
  dutyStart: string | null
  dutyEnd: string | null
  plannedBreakMin: number
  segments: BoardSegment[]
  loads: BoardLoadBar[]
  shiftStart?: string | null
  shiftEnd?: string | null
  onHoliday: boolean
  /** any conflict worth the "conflicts only" filter */
  conflicts: string[]
}

/* ── Onboarding extras (WP-C §6) ───────────────────────────────────────── */

export interface VerifyEmailResponse {
  verified: boolean
  already_verified?: boolean
  user_id?: string
  account_id?: string
}

/* ── Platform / impersonation (SUPERADMIN) ─────────────────────────────── */

export interface PlatformAccount {
  id: string
  account_name: string
  slug: string
  subscription_tier?: string | null
  is_active: boolean
  created_at: string
}

export interface AccountUsage {
  account_id?: string
  users?: number
  drivers?: number
  devices?: number
  loads_total?: number
  loads_30d?: number
  [key: string]: unknown
}

/* ── Performance (assembled from the report catalogue) ─────────────────── */

export interface PerfDriverRow {
  driver_id?: string | null
  driver_name?: string | null
  employee_id?: string | null
  base_location?: string | null
  total_loads: number | null
  completed_loads: number | null
  on_time_pct: number | null
  avg_dwell_minutes: number | null
  total_km: number | null
  active_days: number | null
  scorecard_avg: number | null
}

export interface PerfTrendPoint {
  date: string
  loads_completed: number | null
  on_time_pct: number | null
  unassigned_loads?: number | null
}

export interface PerfKpis {
  onTimePct: number | null
  loadsCompleted: number
  loadsTotal: number
  exceptionsPer100: number | null
  avgDwellMinutes: number | null
  totalKm: number
  driverCount: number
}
